import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Platform } from 'react-native';
import { useExpoModules } from '../../providers/ExpoModulesProvider';
import { log, logError } from '../../utils/logger';

const PushNotificationRegistration = ({ user, actions, translation }) => {
  const { Constants, Device, Notifications } = useExpoModules();

  const registerForPushNotificationsAsync = async () => {
    if (Platform.OS === 'web') return;
    if (!Notifications) {
      logError(new Error('Notifications module is not available'));
      return;
    }
    if (Platform.OS === 'android') {
      // This must be called before we request permissions on Android otherwise the user will not be prompted for permission
      try {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#FF231F7C',
        });
      } catch (error) {
        logError(new Error('Failed to set notification channel', { cause: error }));
      }
    }
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;
    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    if (finalStatus !== 'granted') {
      alert(
        `${translation.push_notification_permissions_denied} ${translation.push_notification_alternate_notifications}`
      );
      return;
    }
    const projectId = Constants?.expoConfig?.extra?.eas?.projectId;
    if (!projectId) {
      alert(
        `${translation.push_notification_setup_error} ${translation.push_notification_alternate_notifications}`
      );
      logError(new Error('Project ID not found when trying to set expo push token'));
      return;
    }
    if (Device?.isDevice) {
      try {
        const pushTokenString = (
          await Notifications.getExpoPushTokenAsync({
            projectId,
          })
        ).data;
        log('Pushing push token', { userId: user.id });
        actions.pushToken(pushTokenString);
      } catch (error) {
        logError(new Error('Cannot get/push expo push token', { cause: error }));
      }
    } else {
      log(
        'Must use physical device for push notifications. No push token was generated or stored for this device.'
      );
    }
  };

  useEffect(() => {
    if (!user.expo_push_token || user.expo_push_token !== user.device_expo_push_token) {
      registerForPushNotificationsAsync();
    }
  }, [user.expo_push_token, user.device_expo_push_token]);

  return null;
};

PushNotificationRegistration.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.object,
  translation: PropTypes.object.isRequired,
};

export default PushNotificationRegistration;
